import {getScrambleTextAnimation} from '@github-ui/brand-core/utils/animation'
import {XIcon} from '@primer/octicons-react'
import {Button, Heading, Link, Text} from '@primer/react'
import {clsx} from 'clsx'
import {AnimatePresence} from 'motion/react'
import {useEffect, useRef, useState} from 'react'

import type {ProductAnnouncementModalView} from './../../ProductAnnouncement/ProductAnnouncement.types'
import ProductAnnouncementModalTabContent from './../ProductAnnouncementModalTabContent/ProductAnnouncementModalTabContent'
import ProductAnnouncementModalVisual from './../ProductAnnouncementModalVisual/ProductAnnouncementModalVisual'

import ModalStyles from './../ProductAnnouncementModal.module.css'
import Styles from './ProductAnnouncementModalDesktop.module.css'

export type ProductAnnouncementModalDesktopProps = ProductAnnouncementModalView

const defaultProps: Partial<ProductAnnouncementModalDesktopProps> = {}

const ProductAnnouncementModalDesktop = (props: ProductAnnouncementModalDesktopProps) => {
  const initializedProps: ProductAnnouncementModalDesktopProps = {...defaultProps, ...props}
  const {
    currentTabIndex,
    tabs,
    onTabChange,
    shouldShowTabCtas,
    shouldShowMainCta,
    seeAll,
    cta,
    title,
    onClose,
    aria,
    onEvent,
  } = initializedProps

  const [hoveredTabIndex, setHoveredTabIndex] = useState<number | null>(null)
  const currentTabIndexRef = useRef<number>(currentTabIndex)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const titleTl = useRef<gsap.core.Timeline | null>(null)

  useEffect(() => {
    if (currentTabIndexRef.current === -1 && headingRef.current) {
      titleTl.current?.kill()
      titleTl.current = getScrambleTextAnimation(headingRef.current)
    }

    currentTabIndexRef.current = currentTabIndex
  }, [currentTabIndex])

  useEffect(() => {
    return () => {
      titleTl.current?.kill()
    }
  }, [])

  return (
    <div className={Styles['ProductAnnouncementModalDesktop']}>
      <button
        className={clsx(
          ModalStyles['ProductAnnouncementModal__closeButton'],
          Styles['ProductAnnouncementModalDesktop__closeButton'],
        )}
        onClick={onClose}
        aria-label={aria.close}
      >
        <XIcon />
      </button>

      <div className={Styles['ProductAnnouncementModalDesktop__sidebar']}>
        <Heading
          ref={headingRef}
          className={ModalStyles['ProductAnnouncementModal__title']}
          as="h2"
          variant="large"
          aria-label={title}
        >
          <span aria-hidden="true">{title}</span>
        </Heading>

        <div className={Styles['ProductAnnouncementModalDesktop__sidebar-content']}>
          <nav
            className={Styles['ProductAnnouncementModalDesktop__sidebar-items']}
            onMouseLeave={() => setHoveredTabIndex(null)}
          >
            <div
              className={Styles['ProductAnnouncementModalDesktop__sidebar-indicator']}
              style={{
                transform: `translateY(calc(${
                  hoveredTabIndex ?? currentTabIndex
                } * (100% + var(--base-size-6) + var(--gap))))`,
              }}
            />
            {tabs.map((tab, index) => {
              return (
                <div
                  className={Styles['ProductAnnouncementModalDesktop__sidebar-item']}
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={`item-${index}`}
                  aria-current={index === currentTabIndex}
                  onMouseEnter={() => setHoveredTabIndex(index)}
                >
                  <button onClick={() => onTabChange(index)} data-tab>
                    <Text weight="medium" size="large">
                      {tab.title}
                    </Text>
                  </button>
                </div>
              )
            })}
          </nav>

          {seeAll && (
            <div className={Styles['ProductAnnouncementModalDesktop__sidebar-seeAll']}>
              <Link
                href={seeAll.href}
                target={seeAll.isNewWindow ? '_blank' : undefined}
                onClick={() => onEvent?.('see_all_clicked')}
              >
                <Text size="large">{seeAll.label}</Text>
              </Link>
            </div>
          )}
        </div>

        {cta && shouldShowMainCta && (
          <div>
            <Button
              as="a"
              href={cta.href}
              variant="primary"
              size="large"
              block
              target={cta.isNewWindow ? '_blank' : undefined}
              onClick={() => onEvent?.('main_cta_clicked')}
            >
              {cta.label}
            </Button>
          </div>
        )}
      </div>

      <div className={Styles['ProductAnnouncementModalDesktop__content']}>
        <div className={Styles['ProductAnnouncementModalDesktop__content-visual']}>
          <ProductAnnouncementModalVisual
            currentTabIndex={currentTabIndex}
            visuals={tabs.map(tab => tab.visual)}
            aria={aria}
          />
        </div>

        <div className={Styles['ProductAnnouncementModalDesktop__content-items']}>
          <AnimatePresence mode="wait">
            {tabs.map(({title: tabTitle, heading, ...tab}, index) => {
              if (index !== currentTabIndex) return null
              return (
                // eslint-disable-next-line @eslint-react/no-array-index-key
                <div key={`item-${index}`}>
                  <ProductAnnouncementModalTabContent
                    {...tab}
                    title={heading}
                    shouldShowTabCtas={!!shouldShowTabCtas}
                    onEvent={(event, data = {}) => onEvent?.(event, {...data, tabName: tabTitle, tabIndex: index})}
                  />
                </div>
              )
            })}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}

export default ProductAnnouncementModalDesktop

try{ ProductAnnouncementModalDesktop.displayName ||= 'ProductAnnouncementModalDesktop' } catch {}